import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/material/styles';
import AppSkeleton, { MainSkeleton } from '@features/AppSkeleton';
import SendInvites from '@features/SendInvites';
import { accountActions, brandsActions } from '@actions';
import ResendEmail from '@features/ResendEmail';
import ServiceWorkerUpdates from '@features/ServiceWorkerUpdates';
import PageTitle from '@features/PageTitle';
import { PAGE_TITLE_HEIGHT } from '@features/PageTitle/styles';
import Sidebar from '@features/Sidebar';
import { SidebarProvider, useSidebar } from '@features/Sidebar/SidebarContext';
import BillingDrawer from '@features/BillingDrawer';
import SubscribeLock from '@features/SubscribeLock';
import { useExportNotifications } from '@hooks';
import TryForFreeModal from '@features/Paywall/TryForFreeModal';
import { RoutePath } from '@constants/routes';
import { getSubscribeState, isAccountLocked } from '@helpers/billing';
import { getHeaderTitleKey } from '@helpers/headerTitleKey';

const Container = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'var(--sidebar-width) calc(100% - var(--sidebar-width))',
    gridTemplateAreas: `"sidebar main" "sidebar main"`,
    '@media (max-width: 1024px)': {
        gridTemplateColumns: '100%',
        gridTemplateAreas: `"sidebar" "main"`,
    },
    '@media print': {
        gridTemplateColumns: '100%',
        gridTemplateAreas: `"main"`,
    },
});

const Main = styled('div')({
    gridArea: 'main',
});

const AppContent = styled('div', {
    shouldForwardProp: prop => prop !== 'pathname' && prop !== 'hasTitle',
})(({ pathname, hasTitle }) => {
    const backgroundColor = pathname === RoutePath.Home ? 'rgb(242, 242, 242)' : 'transparent';
    return {
        '--page-title-height': hasTitle ? `${PAGE_TITLE_HEIGHT}px` : '0px',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        backgroundColor,
        padding: pathname === RoutePath.MessagingInbox ? '13px 24px 0 24px' : 24,
        position: 'relative',
        overflow: 'hidden',
        minHeight: 'var(--100vh)',
        '@media (max-width: 1024px)': {
            '--page-title-height': '0px',
            padding: 0,
            overflow: pathname === RoutePath.MessagingInbox ? 'hidden' : 'auto',
            height: 'calc(var(--100vh) - var(--header-height-mobile))',
            minHeight: 'calc(var(--100vh) - var(--header-height-mobile))',
            top: 'var(--header-height-mobile)',
        },
        '@media print': {
            height: 'auto',
            minHeight: 'unset',
            top: 0,
            position: 'relative',
        },
    };
});

const AppLayoutInner = ({ children }) => {
    const isMobileView = useMediaQuery('@media screen and (max-width: 1024px)', { noSsr: true }); // INFO: to avoid changing the layout when click print button
    const { pathname } = useLocation();
    const contentRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (contentRef.current) contentRef.current.scrollTop = 0;
    }, [pathname]);

    const account = useSelector(state => state.account);
    const user = useSelector(state => state.auth.user);
    const authLoading = useSelector(state => state.auth.loading);
    const { isOpen } = useSelector(state => state.sendInvitesUi);
    const { isOpen: isPricingDrawerOpen } = useSelector(state => state.pricingDrawerUi);
    const dispatch = useDispatch();
    const { isCollapsed } = useSidebar();

    useExportNotifications();

    useEffect(() => {
        if (!account.account && !account.loading && user) {
            dispatch(accountActions.get());
            dispatch(brandsActions.getAll());
        }
    }, [account, user, dispatch]);

    const isLoading = (!account.account && account.loading) || authLoading;

    if (isLoading && isCollapsed) {
        return (
            <Container>
                <Sidebar isMobile={isMobileView} />
                <Main>
                    <MainSkeleton />
                </Main>
            </Container>
        );
    }

    if (isLoading) {
        return <AppSkeleton />;
    }

    const titleKey =
        user && account.account && pathname !== RoutePath.MessagingInbox
            ? getHeaderTitleKey(pathname, account.account)
            : '';

    return (
        <Container>
            {user && account.account ? <Sidebar isMobile={isMobileView} /> : null}
            <Main>
                <AppContent ref={contentRef} pathname={pathname} hasTitle={!!titleKey}>
                    <TryForFreeModal />
                    <ResendEmail user={user} />
                    {user && account.account ? <ServiceWorkerUpdates /> : null}
                    {isPricingDrawerOpen && <BillingDrawer />}
                    {titleKey ? <PageTitle titleKey={titleKey} /> : null}
                    {isAccountLocked(account.account) ? (
                        <SubscribeLock subscribeState={getSubscribeState(account.account)} />
                    ) : (
                        children
                    )}
                </AppContent>
            </Main>
            {isOpen ? <SendInvites isMobile={isMobileView} /> : null}
        </Container>
    );
};

const AppLayout = ({ children }) => (
    <SidebarProvider>
        <AppLayoutInner>{children}</AppLayoutInner>
    </SidebarProvider>
);

export default AppLayout;
